import { z } from 'zod';
import type { BlockDefinition } from '../types';

export const mapEmbedSchema = z.object({
  eyebrow: z.string().max(120).optional(),
  h2: z.string().max(200).optional(),
  provider: z.enum(['openstreetmap', 'google', 'custom']).default('openstreetmap'),
  embed_url: z.string().max(2000).optional(),
  address: z.string().max(300).optional(),
  latitude: z.number().min(-90).max(90).optional(),
  longitude: z.number().min(-180).max(180).optional(),
  zoom: z.number().min(1).max(20).default(15).optional(),
  height: z.number().min(200).max(800).default(420).optional(),
  show_info_box: z.boolean().default(true).optional(),
  info_title: z.string().max(120).optional(),
  info_address: z.string().max(300).optional(),
  info_phone: z.string().max(40).optional(),
  info_email: z.string().max(160).optional(),
});

export type MapEmbedData = z.infer<typeof mapEmbedSchema>;

export function MapEmbedRenderer({
  eyebrow, h2, provider = 'openstreetmap', embed_url, latitude, longitude, zoom = 15, height = 420,
  show_info_box, info_title, info_address, info_phone, info_email,
}: MapEmbedData) {
  let mapSrc = embed_url ?? '';
  if (!mapSrc && provider === 'openstreetmap' && typeof latitude === 'number' && typeof longitude === 'number') {
    const delta = 0.005 / Math.max(1, zoom / 15);
    const bbox = [longitude - delta, latitude - delta, longitude + delta, latitude + delta].join(',');
    mapSrc = `https://www.openstreetmap.org/export/embed.html?bbox=${bbox}&layer=mapnik&marker=${latitude},${longitude}`;
  }

  return (
    <section className="v5-section v5-map-section">
      <div className="container">
        {(eyebrow || h2) && (
          <div className="v5-section-head">
            {eyebrow ? <div className="v5-eyebrow">{eyebrow}</div> : null}
            {h2 ? <h2 className="v5-h2-sm">{h2}</h2> : null}
          </div>
        )}
        <div className="v5-map-wrap" style={{ height: `${height}px` }}>
          {mapSrc ? (
            <iframe
              title={info_title || 'Mapa'}
              src={mapSrc}
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              className="v5-map-iframe"
              style={{ border: 0 }}
              allowFullScreen
            />
          ) : (
            <div className="v5-map-placeholder">Configura la dirección o las coordenadas.</div>
          )}
          {show_info_box && (info_title || info_address || info_phone || info_email) ? (
            <aside className="v5-map-info">
              {info_title ? <h3 className="v5-map-info-title">{info_title}</h3> : null}
              {info_address ? <p className="v5-map-info-line">{info_address}</p> : null}
              {info_phone ? <p className="v5-map-info-line"><a href={`tel:${info_phone}`}>{info_phone}</a></p> : null}
              {info_email ? <p className="v5-map-info-line"><a href={`mailto:${info_email}`}>{info_email}</a></p> : null}
            </aside>
          ) : null}
        </div>
      </div>
    </section>
  );
}

export const mapEmbedBlock: BlockDefinition<MapEmbedData> = {
  meta: {
    type: 'map-embed',
    label: 'Mapa embed',
    description: 'OpenStreetMap embed gratuito con coordenadas, o iframe custom (Google Maps, Mapbox). Caja de info opcional.',
    category: 'media',
    icon: 'map-pin',
  },
  schema: mapEmbedSchema,
  defaults: {
    eyebrow: 'DÓNDE ESTAMOS',
    h2: 'Visítanos',
    provider: 'openstreetmap',
    latitude: 40.4168,
    longitude: -3.7038,
    zoom: 15,
    height: 420,
    show_info_box: true,
    info_title: 'Oficinas centrales',
    info_address: 'Calle Mayor 1, 28001 Madrid',
    info_phone: '+34 900 000 000',
    info_email: 'contacto@ejemplo.com',
  },
  fields: [
    { name: 'eyebrow', label: 'Eyebrow', type: 'text', max: 120 },
    { name: 'h2', label: 'Título', type: 'text', max: 200 },
    {
      name: 'provider',
      label: 'Proveedor',
      type: 'select',
      options: [
        { label: 'OpenStreetMap (gratuito)', value: 'openstreetmap' },
        { label: 'Google Maps', value: 'google' },
        { label: 'Custom (iframe)', value: 'custom' },
      ],
    },
    { name: 'embed_url', label: 'URL embed (Google/custom)', type: 'url', helperText: 'Si usas Google Maps, pega aquí el src del iframe.' },
    { name: 'latitude', label: 'Latitud', type: 'number', min: -90, max: 90 },
    { name: 'longitude', label: 'Longitud', type: 'number', min: -180, max: 180 },
    { name: 'zoom', label: 'Zoom', type: 'number', min: 1, max: 20 },
    { name: 'height', label: 'Altura (px)', type: 'number', min: 200, max: 800 },
    { name: 'show_info_box', label: 'Mostrar caja de info', type: 'boolean' },
    { name: 'info_title', label: 'Info — Título', type: 'text', max: 120 },
    { name: 'info_address', label: 'Info — Dirección', type: 'text', max: 300 },
    { name: 'info_phone', label: 'Info — Teléfono', type: 'text', max: 40 },
    { name: 'info_email', label: 'Info — Email', type: 'email', max: 160 },
  ],
  Renderer: MapEmbedRenderer,
};
